import {
  object,
  string,
  number,
  array,
  Schema,
  ref,
  StringSchema,
  NumberSchema,
  TestContext,
} from "yup";

export const REQUIRED_MSG = "Required field: Please enter a value.";
export const INVALID_MSG = "Invalid value: Please enter a valid value.";
export const CHECK_BOX_MSG = "Required field: Please check the box.";
export const OPTION_MSG = "Required field: Please choose an option.";
export const SELECT_MSG = "Required field: Please select an option.";
// Regular expressions for validation
export const AlphanumericRegex = /^[0-9a-zA-Z]+$/;
export const NotAllowPipeRegex = /^[^|]*$/;
// Improved regex to avoid ReDoS vulnerability
export const EmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Utility function for `oneOf` validation
const oneOfValidation = (message = CHECK_BOX_MSG) => ({
  oneOf: {
    values: ["Y"],
    message,
  },
});

const isEmpty = (val: any) =>
  val === undefined || val === null || val === "";

export const RequiredField = (
  name: string,
  message: string = REQUIRED_MSG,
  required: boolean = true,
  type: "string" | "number" = "string",
  oneOf: boolean = false,
  max?: number,
  maxMessage?: string,
  notOneOf?: boolean,
  notOneOfField?: string,
  notOneOfMessage?: string,
): FieldConfig => ({ /** NOSONAR */
  name,
  message,
  required,
  type,
  ...(oneOf && oneOfValidation(message)),
  ...(max !== undefined && { max, maxMessage }),
  ...(notOneOf && { notOneOf, notOneOfField, notOneOfMessage }),
});

// Utility function for conditional fields
export const ConditionalField = (
  name: string,
  key: string,
  value: string = "Y",
  required: boolean = true,
  message: string = REQUIRED_MSG,
  type: "string" | "number" = "string",
  oneOf: boolean = false,
  max?: number,
  maxMessage?: string,
  notOneOf?: boolean,
  notOneOfField?: string,
  notOneOfMessage?: string,
  min?: number,
  minMessage?: string,
): FieldConfig => ({ /** NOSONAR */
  name,
  type,
  nullable: true,
  when: { key, value, required, message, max, maxMessage, min, minMessage },
  ...(oneOf && oneOfValidation(message)),
  ...(notOneOf && { notOneOf, notOneOfField, notOneOfMessage }),
});

// Utility function for conditional fields with "not equal" condition
// Required when key !== value
export const ConditionalFieldNot = (
  name: string,
  key: string,
  value: string = "N",
  required: boolean = true,
  message: string = REQUIRED_MSG,
  type: "string" | "number" = "string",
  oneOf: boolean = false,
  max?: number,
  maxMessage?: string,
): FieldConfig => ({
  name,
  type,
  nullable: true,
  when: { key, value, isNot: true, required, message },
  ...(oneOf && oneOfValidation(message)),
  ...(max !== undefined && { max, maxMessage }),
});

// Define the field configuration type
export interface WhenConfig {
  key: string;
  value: any;
  isNot?: boolean; // When true, condition is key !== value instead of key === value
  required?: boolean;
  message?: string;
  max?: number;
  maxMessage?: string;
  min?: number;
  minMessage?: string;
  moreThan?: number;
  moreThanMessage?: string;
  lessThan?: number;
  lessThanMessage?: string;
  oneOf?: {
    values?: string[];
    message?: string;
  };
}

export interface FieldConfig {
  name: string;
  type: "string" | "number" | "boolean" | "array";
  required?: boolean;
  message?: string;
  matches?: RegExp;
  matchesMessage?: string;
  max?: number;
  maxMessage?: string;
  min?: number;
  minMessage?: string;
  positive?: boolean;
  integer?: boolean;
  nullable?: boolean;
  notOneOf?: boolean;
  notOneOfField?: string;
  notOneOfMessage?: string;
  moreThan?: number;
  lessThan?: number;
  lessThanMessage?: string;
  moreThanMessage?: string;
  when?: WhenConfig;
  schema?: FieldConfig[];
  isUnique?: boolean; // For array fields to ensure uniqueness
  oneOf?: {
    values?: string[];
    message?: string;
  };
}

const applyStringValidation = (field: any) => {
  let schemaField = string().trim();
  if (field.required) {
    schemaField = schemaField.required(field.message);
  }
  if (field.matches) {
    schemaField = schemaField.matches(field.matches, {
      message: field.matchesMessage,
    });
  }
  if (field.oneOf) {
    // Check if 'oneOf' condition is present
    schemaField = schemaField.oneOf(
      field.oneOf?.values,
      field.oneOf.message ?? "Invalid value"
    );
  }
  if (field.max) {
    schemaField = schemaField.max(field.max, field.maxMessage);
  }
  if (field.min) {
    schemaField = schemaField.min(field.min, field.minMessage);
  }
  if (field.notOneOf) {
    schemaField = schemaField.test(
      "not-one-of",
      field.notOneOfMessage ?? "Both values cannot be equal",
      function (value) {
        const otherValue = this.resolve(ref(field.notOneOfField));
        // Only check if both have value
        if (value !== undefined && value !== "" && otherValue !== undefined && otherValue !== "") {
          return value !== otherValue;
        }
        return true;
      }
    );
  }
  return schemaField;
};

const applyTransform = (field: any) => (value: any, originalValue: any) => {
  if (originalValue === "") {
    return field?.nullable ? null : undefined;
  }
  return value;
};

const applyBasicValidations = (schema: any, field: any) => {
  if (field.nullable) schema = schema.nullable();

  if (field.required) {
    schema = schema
      .required(field.message)
      .positive(INVALID_MSG)
      .integer(INVALID_MSG);
  }

  return schema;
};

const applyNumberConstraints = (schema: any, field: any) => {
  if (field.positive) schema = schema.positive(INVALID_MSG);
  if (field.integer) schema = schema.integer(INVALID_MSG);

  if (field.moreThan) {
    schema = schema.moreThan(field.moreThan, field.moreThanMessage);
  }

  if (field.lessThan) {
    schema = schema.lessThan(field.lessThan, field.lessThanMessage);
  }

  return schema;
};

const applyMaxValidation = (schema: any, field: any) => {
  if (!field.max) return schema;

  return schema.test(
    "len",
    field?.maxMessage,
    (val: any) =>
      val === undefined ||
      val === null ||
      val.toString().length <= field.max
  );
};

const applyMinValidation = (schema: any, field: any) => {
  if (!field.min) return schema;

  return schema.test(
    "min-len",
    field?.minMessage,
    (val: any) =>
      val === undefined ||
      val === null ||
      val.toString().length >= field.min
  );
};

const applyNotOneOfValidation = (schema: any, field: any) => {
  if (!field.notOneOf) return schema;

  return schema.test(
    "not-equal",
    field.notOneOfMessage ?? "Both values cannot be equal",
    function (this: TestContext, value: any) {
      const otherValue = this.parent[field.notOneOfField];

      if (isEmpty(value) || isEmpty(otherValue)) return true;

      return value !== otherValue;
    }
  );
};

const applyNumberValidation = (field: any) => {
  let schemaField: any = number().transform(applyTransform(field));

  schemaField = applyBasicValidations(schemaField, field);
  schemaField = applyNumberConstraints(schemaField, field);
  schemaField = applyMaxValidation(schemaField, field);
  schemaField = applyMinValidation(schemaField, field);
  schemaField = applyNotOneOfValidation(schemaField, field);

  return schemaField;
};

const applyDuplicateEmailValidation = (field: any) => {/** NOSONAR */
  let schemaField: any;
  let innerSchemaFields: { [key: string]: Schema } = {};
  field.schema?.forEach((innerField: FieldConfig) => {
    let innerSchemaField: any;
    innerSchemaField = applyArrayValidation(innerField);
    innerSchemaFields[innerField.name] = innerSchemaField;
  });
  schemaField = array().of(object().shape(innerSchemaFields));
  if (field.required) {
    schemaField = schemaField.required(field.message ?? "Required");
  }

  // Handle uniqueness validation for array fields
  if (Array.isArray(field.schema)) {
    // Find fields in the array schema that have isUnique: true
    const uniqueFields = field.schema.filter((f: any) => f.isUnique);
    const uniqueEmailField = uniqueFields.length === 1 ? uniqueFields[0] : null;
    if (uniqueEmailField) {
      schemaField = schemaField.test(
        "unique-email",
        "Duplicate email addresses are not allowed. Please enter unique email addresses",
        (value: any[]) => {
          if (!Array.isArray(value)) return true;
          const emails = value
            .map((v) => v?.email?.toLowerCase?.().trim?.())
            .filter(Boolean);
          const uniqueSet = new Set(emails);
          return emails.length === uniqueSet.size;
        }
      );
    }
    if (uniqueFields.length > 0) {
      schemaField = schemaField.test(
        "unique-fields",
        `Duplicate email addresses are not allowed. Please enter unique email addresses.`,
        (value: any[]) => {
          if (!Array.isArray(value)) return true;
          for (const uniqueField of uniqueFields) {
            const values = value
              .map((v) => v?.[uniqueField.name]?.toLowerCase?.().trim?.())
              .filter(Boolean);
            const uniqueSet = new Set(values);
            if (values.length !== uniqueSet.size) {
              return false;
            }
          }
          return true;
        }
      );
    }
  }
  return schemaField;
};
const applyValidation = (field: any) => {
  let schemaField: any;
  switch (field.type) {
    case "string":
      schemaField = applyStringValidation(field);
      break;
    case "number":
      schemaField = applyNumberValidation(field);
      break;
    case "array":
      schemaField = applyDuplicateEmailValidation(field);
      break;
    default:
      schemaField = string().trim();
      break;
  }

  return schemaField;
};

const applyArrayValidation = (innerField: FieldConfig) => {
  let innerSchemaField: any;
  switch (innerField.type) {
    case "string":
      innerSchemaField = applyStringValidation(innerField);
      break;
    case "number":
      innerSchemaField = applyNumberValidation(innerField);
      break;
    // Add cases for other types as needed

    default:
      innerSchemaField = string().trim();
      break; 
  }
  if (innerField.when) {
    innerSchemaField = innerSchemaField.when(innerField.when.key, { /** NOSONAR */
      is: innerField.when.value,
      then: (schema: any) => {
        if (innerField.when?.required) {
          schema = schema.required(innerField.when.message ?? "Required");
        }
        // Add other conditions as needed

        return schema;
      },
      otherwise: (schema: any) => schema.notRequired(),
    });
  }
  return innerSchemaField;
};

const applyWhenThenValidations = (schema: any, when: WhenConfig) => {
  if (when?.required) {
    schema = schema.required(when?.message);
  }
  if (when?.max) {
    schema = schema.test(
      "len",
      when?.maxMessage,
      (val: string | number) =>
        val === undefined || val.toString().length <= when?.max!
    );
  }
  if (when?.min) {
    schema = schema.test(
      "min-len",
      when?.minMessage,
      (val: string | number) =>
        val === undefined || val === null || val.toString().length >= when?.min!
    );
  }
  if (when?.oneOf) {
    schema = schema.oneOf(
      when?.oneOf?.values,
      when?.oneOf?.message ?? "Invalid value"
    );
  }
  if (when?.moreThan) {
    schema = schema.moreThan(when?.moreThan, when?.moreThanMessage);
  }
  if (when?.lessThan) {
    schema = schema.lessThan(when?.lessThan, when?.lessThanMessage);
  }
  return schema;
};

const applyWhenCondition = (schemaField: any, field: any) => {
  const { key, value, isNot } = field.when;
  return schemaField?.when(key, {
    // For "not" conditions, only trigger when the parent has a real value.
    is: isNot ? (val: any) => !isEmpty(val) && val !== value : value,
    then: (schema: any) => applyWhenThenValidations(schema, field.when),
    otherwise: (schema: StringSchema | NumberSchema) => schema.notRequired(),
  });
};

export const CreateDynamicSchema = (config: any) => {
  const schemaFields: any = {};
  config.forEach((field: any) => {
    let schemaField = applyValidation(field);
    if (field.when) {
      schemaField = applyWhenCondition(schemaField, field);
    }
    schemaFields[field.name] = schemaField;
  });

  return object().shape(schemaFields);
};
